// Klientfilter for komplette regnskapseksporter. ZIP-en leses direkte fra brukerens
// lokale File/Blob, og bare datafilene som backend faktisk kan tolke sendes videre.
// Originalfilen endres aldri.

const ACCOUNTING_IMPORT_ZIP_SCRIPT = 'https://cdn.jsdelivr.net/npm/@zip.js/zip.js@2.8.42/dist/zip.min.js';
// SRI: ny versjon = ny hash (openssl dgst -sha384 -binary | openssl base64 -A).
const ACCOUNTING_IMPORT_ZIP_INTEGRITY = 'sha384-cdLKQ7JW+89d4KPS7KM3KqJZKBjOMhUCYkFOcAuQM40z4RdqrD6e3C6iBZqZVA79';

const accountingImportPath = (value) => String(value || '').replace(/\\/g, '/').replace(/^\/+/, '');

function isAccountingImportDataZipEntry(path) {
  const normalized = accountingImportPath(path).toLowerCase();
  if (!normalized || normalized.endsWith('/')) return false;
  if (normalized.split('/').some((segment) => !segment || segment === '.' || segment === '..')) return false;
  if (normalized.endsWith('.csv') || normalized.endsWith('.xlsx')) return true;
  return normalized.endsWith('.xml') && normalized.includes('saf-t');
}

function accountingImportDataOnlyFileName(name) {
  const normalized = String(name || 'regnskapseksport.zip').trim() || 'regnskapseksport.zip';
  return /\.zip$/i.test(normalized)
    ? normalized.replace(/\.zip$/i, '-kun-data.zip')
    : `${normalized}-kun-data.zip`;
}

function loadAccountingImportZipLib() {
  if (window.zip?.ZipReader && window.zip?.ZipWriter) return Promise.resolve(window.zip);
  if (window.__plummeAccountingImportZipLoading) return window.__plummeAccountingImportZipLoading;
  window.__plummeAccountingImportZipLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ACCOUNTING_IMPORT_ZIP_SCRIPT;
    script.async = true;
    script.integrity = ACCOUNTING_IMPORT_ZIP_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.onload = () => window.zip?.ZipReader
      ? resolve(window.zip)
      : reject(new Error(t('set.import.dataOnlyLibraryFailed')));
    script.onerror = () => {
      window.__plummeAccountingImportZipLoading = null;
      reject(new Error(t('set.import.dataOnlyLibraryFailed')));
    };
    document.head.appendChild(script);
  });
  return window.__plummeAccountingImportZipLoading;
}

async function filterAccountingImportZipFile(file, { zipLib, onProgress } = {}) {
  const library = zipLib || await loadAccountingImportZipLib();
  const reader = new library.ZipReader(new library.BlobReader(file), { useWebWorkers: false });
  let entries;
  try {
    entries = await reader.getEntries();
    const files = entries.filter((entry) => !entry.directory);
    const keptEntries = files.filter((entry) => isAccountingImportDataZipEntry(entry.filename));
    if (!keptEntries.length) throw new Error(t('set.import.dataOnlyNoFiles'));

    const removedEntries = files.length - keptEntries.length;
    const keptSourceBytes = keptEntries.reduce((sum, entry) => sum + Number(entry.uncompressedSize || 0), 0);
    const removedSourceBytes = files
      .filter((entry) => !isAccountingImportDataZipEntry(entry.filename))
      .reduce((sum, entry) => sum + Number(entry.uncompressedSize || 0), 0);

    if (!removedEntries) {
      return {
        file,
        stats: { keptEntries: keptEntries.length, removedEntries: 0, keptSourceBytes, removedSourceBytes: 0, originalBytes: Number(file.size || 0), filteredBytes: Number(file.size || 0) },
      };
    }

    const writer = new library.ZipWriter(new library.BlobWriter('application/zip'), {
      level: 6,
      useWebWorkers: false,
    });
    for (let index = 0; index < keptEntries.length; index += 1) {
      const entry = keptEntries[index];
      onProgress?.({
        phase: 'filtering',
        fileName: file.name,
        entryName: entry.filename,
        entryIndex: index + 1,
        entryCount: keptEntries.length,
        percent: Math.round((index / keptEntries.length) * 100),
      });
      const content = await entry.getData(new library.BlobWriter(), { useWebWorkers: false });
      await writer.add(accountingImportPath(entry.filename), new library.BlobReader(content), {
        lastModDate: entry.lastModDate,
        level: 6,
        useWebWorkers: false,
      });
    }
    const filteredBlob = await writer.close();
    const filteredFile = new File([filteredBlob], accountingImportDataOnlyFileName(file.name), {
      type: 'application/zip',
      lastModified: Number(file.lastModified || Date.now()),
    });
    onProgress?.({ phase: 'filtering', fileName: filteredFile.name, percent: 100 });
    return {
      file: filteredFile,
      stats: {
        keptEntries: keptEntries.length,
        removedEntries,
        keptSourceBytes,
        removedSourceBytes,
        originalBytes: Number(file.size || 0),
        filteredBytes: Number(filteredFile.size || 0),
      },
    };
  } finally {
    await reader.close().catch(() => {});
  }
}

async function prepareAccountingImportFiles({ files = [], dataOnly = false, zipLib, onProgress } = {}) {
  const inputFiles = Array.from(files || []).filter(Boolean);
  if (!dataOnly) return { files: inputFiles, stats: null };
  const outputFiles = [];
  const totals = { keptEntries: 0, removedEntries: 0, keptSourceBytes: 0, removedSourceBytes: 0, originalBytes: 0, filteredBytes: 0 };
  for (let index = 0; index < inputFiles.length; index += 1) {
    const file = inputFiles[index];
    const isZip = String(file.name || '').toLowerCase().endsWith('.zip');
    const result = isZip
      ? await filterAccountingImportZipFile(file, { zipLib, onProgress })
      : { file, stats: { keptEntries: 1, removedEntries: 0, keptSourceBytes: Number(file.size || 0), removedSourceBytes: 0, originalBytes: Number(file.size || 0), filteredBytes: Number(file.size || 0) } };
    outputFiles.push(result.file);
    Object.keys(totals).forEach((key) => { totals[key] += Number(result.stats?.[key] || 0); });
  }
  return { files: outputFiles, stats: totals };
}

Object.assign(window, {
  PlummeAccountingImportClientFilter: {
    accountingImportDataOnlyFileName,
    filterAccountingImportZipFile,
    isAccountingImportDataZipEntry,
    prepareAccountingImportFiles,
  },
});
